import {
  type ConfigurableOptionsResult,
  type SelectedOptionInput,
  fetchConfiguration
} from './configurable-options-api';
import type { ConfiguratorConfig } from './configurator-config';

/**
 * The single source of availability. Callers work with a snapshot and never
 * learn whether it came from a round trip or a preloaded matrix, so swapping
 * the endpoint's shape touches only this file.
 */

export interface ValueAvailability {
  uid: string;
  valueIndex: number;
  label: string;
  isSelected: boolean;
  isAvailable: boolean;
}

export interface GroupAvailability {
  attributeCode: string;
  values: ValueAvailability[];
}

export interface AvailabilitySnapshot {
  groups: GroupAvailability[];
  currency: string;
  lowestFinalPrice?: number;
  isComplete: boolean;
  matchedSku?: string;
}

export type Selection = ReadonlyMap<string, number>;

function selectionKey(selection: Selection): string {
  return [...selection.entries()]
    .map(([attributeCode, valueIndex]) => `${attributeCode}:${valueIndex}`)
    .sort()
    .join('|');
}

export function toSelectedOptions(selection: Selection): SelectedOptionInput[] {
  return [...selection.entries()].map(([attributeCode, valueIndex]) => ({
    attributeCode,
    valueIndex
  }));
}

/**
 * Folds the narrowed API response back over the full CMS catalogue, so an
 * unreachable value renders greyed out instead of vanishing from the grid.
 */
export function buildSnapshot(
  config: ConfiguratorConfig,
  response: ConfigurableOptionsResult
): AvailabilitySnapshot {
  const allowedByAttribute = new Map(
    response.options.map(option => [option.attributeCode, option.values])
  );

  const groups = config.pages.flatMap(page =>
    page.groups.map<GroupAvailability>(group => {
      const allowed = allowedByAttribute.get(group.attributeCode) ?? [];
      const allowedByIndex = new Map(allowed.map(value => [value.valueIndex, value]));

      return {
        attributeCode: group.attributeCode,
        values: group.values.map<ValueAvailability>(value => {
          const match = allowedByIndex.get(value.valueIndex);

          return {
            uid: value.uid,
            valueIndex: value.valueIndex,
            label: value.label,
            isSelected: match?.isSelected ?? false,
            isAvailable: match !== undefined
          };
        })
      };
    })
  );

  return {
    groups,
    currency: response.currency,
    lowestFinalPrice: response.lowestFinalPrice,
    isComplete: response.isComplete,
    matchedSku: response.matchedSku
  };
}

export interface AvailabilityResolver {
  get: (selection: Selection, signal: AbortSignal) => Promise<AvailabilitySnapshot>;
  clear: () => void;
}

export function createAvailabilityResolver(
  apiBaseUrl: string,
  productGuid: string,
  config: ConfiguratorConfig
): AvailabilityResolver {
  const cache = new Map<string, AvailabilitySnapshot>();

  return {
    get: async (selection, signal) => {
      const key = selectionKey(selection);
      const cached = cache.get(key);

      if (cached) {
        return cached;
      }

      const response = await fetchConfiguration(
        apiBaseUrl,
        productGuid,
        toSelectedOptions(selection),
        signal
      );
      const snapshot = buildSnapshot(config, response);
      cache.set(key, snapshot);

      return snapshot;
    },
    clear: () => cache.clear()
  };
}

/** Drops choices the latest snapshot no longer allows. */
export function pruneSelection(
  selection: Selection,
  snapshot: AvailabilitySnapshot
): Map<string, number> {
  const pruned = new Map(selection);

  snapshot.groups.forEach(group => {
    const chosen = pruned.get(group.attributeCode);

    if (chosen === undefined) {
      return;
    }

    const stillAllowed = group.values.some(
      value => value.valueIndex === chosen && value.isAvailable
    );

    if (!stillAllowed) {
      pruned.delete(group.attributeCode);
    }
  });

  return pruned;
}
